import axios from 'axios';
import Cookies from 'universal-cookie';
import { getSimulation } from './localStorage';
import { debugLogger } from './debugLogger';

/**
 * Creates a promise that rejects after a specified timeout
 * @param {number} ms - Timeout in milliseconds
 * @param {string} operationName - Name of the operation for error message
 * @returns {Promise} Promise that rejects with timeout error
 */
const createTimeoutPromise = (ms, operationName = 'Operation') => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      const error = new Error(`${operationName} timed out after ${ms}ms`);
      error.isTimeout = true;
      reject(error);
    }, ms);
  });
};

/**
 * Wraps a promise with a timeout
 * @param {Promise} promise - Promise to wrap
 * @param {number} timeout - Timeout in milliseconds (default: 30000)
 * @param {string} operationName - Name of the operation for error message
 * @returns {Promise} Promise that resolves/rejects with timeout
 */
export const withTimeout = (promise, timeout = 30000, operationName = 'Operation') => {
  return Promise.race([
    promise,
    createTimeoutPromise(timeout, operationName)
  ]);
};

export const getBaseUrl = () => {
  return import.meta.env.VITE_API_URL;
};

// Add axios interceptors once to avoid duplicates under hot-reload/dev tooling.
// Guard with optional chaining so Jest mocks that don't provide an interceptors
// API don't crash at module load time.
if (!axios.__SIM_INTERCEPTORS_REGISTERED && axios.interceptors?.request?.use && axios.interceptors?.response?.use) {
  // Add axios interceptor to inject simulation headers AND log requests
  axios.interceptors.request.use((config) => {
    // Add simulation headers
    const simulation = getSimulation();
    if (simulation) {
      config.headers = config.headers ?? {};
      if (simulation.simulatedRole != null) {
        config.headers['X-Simulated-Role'] = simulation.simulatedRole;
      }
      if (simulation.simulatedOrg != null) {
        config.headers['X-Simulated-Organization'] = simulation.simulatedOrg.id;
      }
    }
    
    // Log the outgoing request
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    config.metadata = { requestId, startTime: Date.now() };
    
    debugLogger.log('API_REQUEST', 'Outgoing API request', {
      requestId,
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL || ''}${config.url}`,
      hasAuth: !!config.headers?.Authorization,
      hasData: !!config.data,
      dataSize: config.data ? JSON.stringify(config.data).length : 0,
      params: config.params,
      headers: {
        'Content-Type': config.headers?.['Content-Type'],
        'X-Simulated-Role': config.headers?.['X-Simulated-Role'],
        'X-Simulated-Organization': config.headers?.['X-Simulated-Organization']
      }
    });
    
    return config;
  }, (error) => {
    debugLogger.error('API_REQUEST', 'Request interceptor error', error);
    return Promise.reject(error);
  });

  // Add axios response interceptor to handle project access errors AND log responses
  axios.interceptors.response.use(
    (response) => {
      // Log successful response
      const duration = response.config.metadata?.startTime 
        ? Date.now() - response.config.metadata.startTime 
        : null;
      
      debugLogger.log('API_RESPONSE', 'API response received', {
        requestId: response.config.metadata?.requestId,
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        statusText: response.statusText,
        duration: duration ? `${duration}ms` : 'unknown',
        dataSize: response.data ? JSON.stringify(response.data).length : 0,
        hasData: !!response.data,
        dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
        dataCount: Array.isArray(response.data) ? response.data.length : null
      });
      
      return response;
    },
    (error) => {
      // Log error response
      const duration = error.config?.metadata?.startTime 
        ? Date.now() - error.config.metadata.startTime 
        : null;
      
      debugLogger.error('API_RESPONSE', 'API request failed', {
        requestId: error.config?.metadata?.requestId,
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        duration: duration ? `${duration}ms` : 'unknown',
        errorMessage: error.message,
        errorData: error.response?.data,
        isTimeout: error.isTimeout,
        isNetworkError: !error.response
      });
      
      // Handle 401 Unauthorized — token expired or invalid, redirect to login.
      // Skip this on /set-password and /reset-password — those pages manage their
      // own auth state (an admin may be setting up an account for someone else
      // while they themselves are already logged in, and the session cookie swap
      // during auth.login() can trigger a spurious 401 from in-flight requests).
      if (error.response && error.response.status === 401) {
        const currentPath = window.location.pathname;
        const isPasswordPage =
          currentPath === '/set-password' || currentPath === '/reset-password';

        if (isPasswordPage) {
          debugLogger.warn('API_RESPONSE', 'Unauthorized (401) on password page — suppressing session-expired redirect', {
            url: error.config?.url,
            status: 401,
            path: currentPath,
          });
          return Promise.reject(error);
        }

        debugLogger.warn('API_RESPONSE', 'Unauthorized (401) - session expired, redirecting to login', {
          url: error.config?.url,
          status: 401,
        });

        // Clear the auth cookie so AuthContext reinitialises cleanly on the next
        // login attempt. Intentionally preserve activeProjectId so the user's
        // last active project and its filters are restored after re-login.
        const cookies = new Cookies(null, { path: '/' });
        cookies.remove('activeUser', { path: '/' });

        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login?reason=session_expired';
        }

        return Promise.reject(error);
      }

      // Handle 403/404 errors for project-selection-related endpoints
      if (error.response && (error.response.status === 403 || error.response.status === 404)) {
        const url = error.config?.url || '';

        // Only react to endpoints that are definitively project-selection related.
        // Narrowing /changes/ to /changes/project/ avoids false redirects from
        // individual change operations (delete, comments, audit-trail) that can
        // legitimately return 404.
        const isProjectSelectionEndpoint =
          url.includes('/projects-data/') ||
          url.includes('/projects-aggregations/') ||
          url.includes('/projects/') ||
          url.includes('/changes/project/');

        // Only redirect when the failing URL references the current active project
        const cookies = new Cookies(null, { path: '/' });
        const activeProjectId = cookies.get('activeProjectId');
        const urlContainsActiveProjectId =
          !!activeProjectId && url.includes(String(activeProjectId));

        if (isProjectSelectionEndpoint && urlContainsActiveProjectId) {
          debugLogger.warn('API_RESPONSE', 'Project access denied - redirecting to dashboard', {
            url,
            status: error.response.status,
            activeProjectId
          });
          
          // Clear the active project cookie
          cookies.remove('activeProjectId', { path: '/' });

          // Redirect to dashboard (project selector)
          if (window.location.pathname !== '/dashboard') {
            window.location.href = '/dashboard';
          }
        }
      }
      
      return Promise.reject(error);
    }
  );

  axios.__SIM_INTERCEPTORS_REGISTERED = true;
}

/**
 * Validates required parameters before making API calls
 * @param {Object} params - Object with parameter names as keys and values to validate
 * @param {String} functionName - Name of the calling function for error messages
 * @throws {Error} If any required parameter is missing, null, undefined, or empty string
 */
const validateParams = (params, functionName) => {
  const invalidParams = [];
  
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      invalidParams.push(key);
    }
  });
  
  if (invalidParams.length > 0) {
    const error = new Error(
      `${functionName}: Missing or invalid required parameters: ${invalidParams.join(', ')}`
    );
    error.name = 'ValidationError';
    error.invalidParams = invalidParams;
    console.error(error.message);
    throw error;
  }
};

const baseUrl = getBaseUrl();
const loginUrl = `${baseUrl}/login/`;
const userUrl = `${baseUrl}/users/`;
const organizationsUrl = `${baseUrl}/organizations/`;
const projectsUrl = `${baseUrl}/projects/`;
const projectsDataUrl = `${baseUrl}/projects-data/`;
const projectsAggregationUrl = `${baseUrl}/projects-aggregations/`;
const changesUrl = `${baseUrl}/changes/`;
const backupExportsUrl = `${baseUrl}/backup-export/`;
const forgotPasswordUrl = `${baseUrl}/reset-password/`;
const masterStatsUrl = `${baseUrl}/master-stats/`;
const inviteUrl = `${baseUrl}/invite/`;


/**********************************
 *                                *
 *            LOGIN               *
 *                                *
 *********************************/

/**
 * POST
 * @param {String} email User login email
 * @param {String} password User login password
 * @returns Resolved login promise
 */
export const login = async (email, password) => {
  debugLogger.log('API_AUTH', 'Login attempt', { email });
  
  try {
    const response = await axios({
      data: {
        email: email,
        password: password,
      },
      headers: { 'Content-type': 'application/json' },
      method: 'POST',
      url: loginUrl,
    });
    
    debugLogger.log('API_AUTH', 'Login successful', { 
      email, 
      userId: response.data?.user?.id 
    });
    
    return response;
  } catch (error) {
    debugLogger.error('API_AUTH', 'Login failed', error);
    return error;
  }
};


/**********************************
 *                                *
 *         RESET PASSWORD         *
 *                                *
 *********************************/

/**
 * Sends a password reset token to the user's email address.
 *
 * This function makes a POST request to the "generate-reset-token" endpoint.
 * It passes the user's email in the request body so that the backend can generate
 * a reset token and send a password reset email to the user.
 *
 * @param {string} email - The user's login email.
 * @returns {Promise} A promise that resolves with the response from the token generation endpoint.
 */
export const forgotPassword = async (email) => {
  try {
    const response = await axios({
      data: { email },
      headers: { 'Content-type': 'application/json' },
      method: 'POST',
      url: `${forgotPasswordUrl}generate-token`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Resets the user's password using the provided reset token.
 *
 * This function makes a POST request to the "reset-password" endpoint, sending
 * the reset token and the new password in the request body. The backend verifies
 * the token and, if valid, updates the user's password.
 *
 * @param {string} token - The password reset token received via email.
 * @param {string} password - The new password to be set for the user.
 * @returns {Promise} A promise that resolves with the response from the password reset endpoint.
 */
export const resetPassword = async (token, password) => {
  try {
    const response = await axios({
      data: {
        password_reset_token: token,
        new_password: password,
      },
      headers: { 'Content-type': 'application/json' },
      method: 'POST',
      url: `${forgotPasswordUrl}`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};


/**********************************
 *                                *
 *            USERS               *
 *                                *
 *********************************/

/**
 * GET all users for the current admin's organization.
 * @param {string} accessToken - Admin JWT
 * @returns {Promise} Resolves with array of user objects
 */
export const getAllUsers = async (accessToken) => {
  try {
    validateParams({ accessToken }, 'getAllUsers');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: userUrl,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * POST
 * @param {Object} userData
 * @param {String} accessToken Auth token
 * @returns Newly created user object
 */
export const newUser = async (userData, accessToken) => {
  try {
    const response = await axios({
      data: {
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        user_type: userData.userType,
        first_time_sign_in: true,
        organization_id: userData.organization,
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: userUrl,
    });
    return response;
  } catch (error) {
    // Re-throw so callers can handle HTTP errors (4xx/5xx) explicitly.
    // The error.response object contains status and data from the server.
    throw error;
  }
};

/**
 * GET multi
 * @param {Array} users user IDs
 * @param {String} accessToken auth token
 * @returns Multiple user objects
 */
export const getMultiUsers = async (users, accessToken) => {
  try{
    let endpoints = [];
    users.forEach((user) => {
      endpoints.push(`${userUrl}${user}`);
    });
    const requests = endpoints.map((endpoint) => {
      return axios.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
      });
    });
    const response = await axios.all(requests, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
    });
    return response;
  }
  catch(error){
    return error;
  }
}

/**
 * PUT
 * @param {Object} userData
 * @param {String} accessToken Auth token
 * @returns Newly created user object
 */
export const editUser = async (userData, accessToken) => {
  try {
    const response = await axios({
      data: userData,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PUT',
      url: userUrl,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * DELETE
 * @param {Number} userId
 * @param {String} accessToken Auth token
 * @returns Success or error message
 */
export const deleteUser = async (userId, accessToken) => {
  // console.log(userId);
  try {
    validateParams({ userId, accessToken }, 'deleteUser');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${userUrl}${userId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**********************************
 *                                *
 *         ORGANIZATIONS          *
 *                                *
 *********************************/

/**
 * POST
 * @param {String} organization Organization name
 * @param {String} accessToken Auth token
 * @returns Newly created organization object
 */
export const newOrganization = async (organization, accessToken) => {
  try {
    const response = await axios({
      data: {
        name: organization,
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: organizationsUrl,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * GET multi
 * @param {String} accessToken auth token
 * @returns Array of all organizations
 */
export const getOrganizations = async (accessToken) => {
  try {
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: organizationsUrl,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * GET one
 * @param {Number} id Organization id
 * @param {String} accessToken auth token
 * @returns Organization details
 */
export const getOrganization = async (id, accessToken) => {
  try {
    validateParams({ id, accessToken }, 'getOrganization');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${organizationsUrl}${id}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * DELETE
 * @param {Number} organizationId
 * @param {String} accessToken Auth token
 * @returns Success or error message
 */
export const deleteOrganization = async (organizationId, accessToken) => {
  try {
    validateParams({ organizationId, accessToken }, 'deleteOrganization');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${organizationsUrl}${organizationId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**********************************
 *                                *
 *           PROJECTS             *
 *                                *
 *********************************/

/**
 * POST
 * @param {Object} projectInfo Project details
 * @param {String} accessToken auth token
 * @returns Newly created project
 */
export const createProject = async (projectInfo, accessToken) => {
  debugLogger.log('API_PROJECT', 'Creating project', { 
    projectTitle: projectInfo.projectTitle,
    organizationId: projectInfo.organizationId,
    startDate: projectInfo.startDate,
    endDate: projectInfo.endDate,
    accountCount: projectInfo.accounts?.length
  });
  
  try {
    const response = await axios({
      data: {
        'created_by': projectInfo.userId,
        'end_date': projectInfo.endDate,
        'marked_for_deletion': false,
        'organization_id': projectInfo.organizationId,
        'accounts': projectInfo.accounts,
        'project_title': projectInfo.projectTitle,
        'start_date': projectInfo.startDate
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json' 
      },
      method: 'POST',
      url: projectsUrl,
    });
    
    debugLogger.log('API_PROJECT', 'Project created successfully', { 
      projectId: response.data?.id,
      projectTitle: projectInfo.projectTitle
    });
    
    return response;
  } catch (error) {
    debugLogger.error('API_PROJECT', 'Failed to create project', error);
    return error;
  }
};

/**
 * GET one project dataset
 * @param {Number} id Project id
 * @param {String} accessToken auth token
 * @returns Project details
 */
export const getProject = async (id, accessToken) => {
  debugLogger.log('API_PROJECT', 'Loading project', { projectId: id });
  
  try {
    validateParams({ id, accessToken }, 'getProject');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsUrl}${id}`,
    });
    
    debugLogger.log('API_PROJECT', 'Project loaded successfully', { 
      projectId: id,
      accountCount: response.data?.accounts?.length,
      entityCount: response.data?.entities?.length
    });
    
    return response;
  } catch (error) {
    debugLogger.error('API_PROJECT', 'Failed to load project', error);
    return error;
  }
};

/**
 * GET one project general information
 * @param {Number} id Project id
 * @param {String} accessToken auth token
 * @returns Project information
 */
export const getProjectGeneralInformation = async (id, accessToken) => {
  try {
    validateParams({ id, accessToken }, 'getProjectGeneralInformation');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsUrl}general-information/${id}`,
    })
    return response
  } catch (error) {
    return error
  }
}

/**
 * GET one project aggregation dataset
 * @param {Number} id Project id
 * @param {String} accessToken auth token
 * @returns Project aggregation details
 */
export const getProjectParentAccountsTotals = async (id, accessToken) => {
  try {
    validateParams({ id, accessToken }, 'getProjectParentAccountsTotals');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsAggregationUrl}parent-accounts/${id}`,
    })
    return response
  } catch (error) {
    return error
  }
}

export const getProjectAccountsTotals = async (id, accessToken) => {
  try {
    validateParams({ id, accessToken }, 'getProjectAccountsTotals');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsAggregationUrl}accounts/${id}`,
    })
    return response
  } catch (error) {
    return error
  }
}

/**
 * GET multi
 * @param {Array} projects project IDs
 * @param {String} accessToken auth token
 * @returns Multiple project objects
 */
export const getMultiProjects = async (projects, accessToken) => {
  try{
    let endpoints = [];
    if(projects && projects.length > 1){
      projects.forEach((project) => {
        endpoints.push(`${projectsUrl}${project.id}`);
      });
      const requests = endpoints.map((endpoint) => {
        return axios.get(endpoint, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-type': 'application/json',
          },
        });
      });
      const response = await axios.all(requests, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
      });
      return response;
    }
    return false;
  }
  catch(error){
    return error;
  }
}

/**
 * GET Projects associated with a user
 * @param {Number} userId
 * @param {Number} organizationId
 * @param {String} accessToken
 * @returns Project details
 */
export const getUserProjectsList = async (userId, organizationId, accessToken) => {
  try {
    validateParams({ userId, organizationId, accessToken }, 'getUserProjectsList');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsUrl}user-project-list/${userId}/${organizationId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * GET All Projects associated with an organization
 * @param {Number} organizationId
 * @param {String} accessToken
 * @returns Project details
 */
export const getOrganizationProjectsList = async (organizationId, accessToken) => {
  try {
    validateParams({ organizationId, accessToken }, 'getOrganizationProjectsList');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsUrl}by-organization/${organizationId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * DELETE
 * @param {Number} projectId
 * @param {String} accessToken Auth token
 * @returns Success or error message
 */
export const deleteProject = async (projectId, accessToken) => {
  try {
    validateParams({ projectId, accessToken }, 'deleteProject');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${projectsUrl}${projectId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * PATCH — Archive a project (soft-delete)
 * @param {Number} projectId
 * @param {String} accessToken Auth token
 * @returns Updated project with archived_on set
 */
export const archiveProject = async (projectId, accessToken) => {
  try {
    validateParams({ projectId, accessToken }, 'archiveProject');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PATCH',
      url: `${projectsUrl}${projectId}/archive`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * PATCH — Restore an archived project
 * @param {Number} projectId
 * @param {String} accessToken Auth token
 * @returns Updated project with archived_on cleared
 */
export const restoreProject = async (projectId, accessToken) => {
  try {
    validateParams({ projectId, accessToken }, 'restoreProject');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PATCH',
      url: `${projectsUrl}${projectId}/restore`,
    });
    return response;
  } catch (error) {
    return error;
  }
};



export const getCheckIfProjectTitleExists = async (projectTitle, accessToken) => { 
  try {
    const response = await axios.get(`${projectsUrl}check-project-title`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      params: {
        project_title: projectTitle
      }
    });
    return response;
  } catch (error) {
    return error;
  }
};





/**********************************
 *                                *
 *         PROJECTS DATA          *
 *                                *
 *********************************/

/**
 * Create a new account (GL Key)
 * POST /projects-data/
 * @param {Object} accountData - Full account object
 * @param {String} accessToken - Auth token
 */
export const createAccount = async (accountData, accessToken) => {
  try {
    validateParams({ accountData, accessToken }, 'createAccount');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${projectsDataUrl}`,
      data: accountData,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * GET one project account data
 * @param {Number} id Project id
 * @param {String} glKey Account GLkey
 * @param {String} accessToken auth token
 * @returns Project account data
 */
export const getProjectDataByGlKey = async (id, glKey, accessToken) => {
  try {
    validateParams({ id, glKey, accessToken }, 'getProjectDataByGlKey');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsDataUrl}by-gl-key/${id}/${glKey}`,
    })
    return response
  } catch (error) {
    return error
  }
}

/**
 * Bulk fetch full account data (including monthly dates) for a list of GL keys.
 * Used to eagerly load all accounts referenced by changes so calculations work
 * regardless of lazy-load state.
 *
 * POST /projects-data/{projectId}/bulk-gl-keys
 * @param {Number} projectId - Project ID
 * @param {string[]} glKeys - Array of GL key strings
 * @param {String} accessToken - Auth token
 * @returns { accounts: [...], found: number, requested: number, missing: [...] }
 */
export const getAccountsByGlKeys = async (projectId, glKeys, accessToken) => {
  try {
    validateParams({ projectId, glKeys, accessToken }, 'getAccountsByGlKeys');
    if (!glKeys || glKeys.length === 0) {
      return { data: { accounts: [], found: 0, requested: 0, missing: [] }, status: 200 };
    }
    const response = await withTimeout(
      axios({
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
        method: 'POST',
        url: `${projectsDataUrl}${projectId}/bulk-gl-keys`,
        data: { gl_keys: glKeys },
      }),
      30000,
      'getAccountsByGlKeys'
    );
    return response;
  } catch (error) {
    debugLogger.error('API_PROJECT', 'Failed to bulk fetch GL key accounts', error);
    return error;
  }
};

/**
 * Validate GL Key uniqueness
 * GET
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {String} entityId - Entity ID
 * @param {String} departmentId - Department ID
 * @param {String} accountId - Account ID
 * @param {Number} excludeId - Optional ID to exclude from check (for edits)
 * @returns { exists: boolean }
 */
export const validateGLKeyUniqueness = async (accessToken, projectId, entityId, departmentId, accountId, excludeId = null) => {
  try {
    validateParams({ accessToken, projectId, entityId, departmentId, accountId }, 'validateGLKeyUniqueness');
    const params = {
      entity_id: entityId,
      department_id: departmentId,
      account_id: accountId
    };
    if (excludeId !== null) {
      params.exclude_id = excludeId;
    }
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsDataUrl}validate-gl-key/${projectId}`,
      params: params
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Validate account name uniqueness within taxonomy
 * GET
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {String} entityId - Entity ID
 * @param {String} departmentId - Department ID
 * @param {String} parentAccount - Parent account identifier
 * @param {String} accountName - Account name to validate
 * @param {Number} excludeId - Optional ID to exclude from check (for edits)
 * @returns { exists: boolean }
 */
export const validateAccountNameInTaxonomy = async (accessToken, projectId, entityId, departmentId, parentAccount, accountName, excludeId = null) => {
  try {
    validateParams({ accessToken, projectId, entityId, departmentId, parentAccount, accountName }, 'validateAccountNameInTaxonomy');
    const params = {
      entity_id: entityId,
      department_id: departmentId,
      parent_account: parentAccount,
      account_name: accountName
    };
    if (excludeId !== null) {
      params.exclude_id = excludeId;
    }
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsDataUrl}validate-account-name/${projectId}`,
      params: params
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Check GL Key dependencies before deletion
 * GET
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {String} glKey - GL Key to check (format: EntityID-DepartmentID-AccountID)
 * @returns Dependency information with drivers, changes, and formulas
 */
export const checkGLKeyDependencies = async (accessToken, projectId, glKey) => {
  try {
    validateParams({ accessToken, projectId, glKey }, 'checkGLKeyDependencies');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsDataUrl}dependencies/${projectId}/${glKey}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Bulk delete GL Keys
 * POST
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {Array} accountIds - Array of account IDs to delete
 * @returns { deleted: [ids], failed: [{ id, gl_key, reason, dependencies }] }
 */
export const bulkDeleteGLKeys = async (accessToken, projectId, accountIds) => {
  try {
    validateParams({ accessToken, projectId, accountIds }, 'bulkDeleteGLKeys');
    const response = await axios({
      data: {
        project_id: projectId,
        account_ids: accountIds
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${projectsDataUrl}bulk-delete`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Bulk update GL Keys
 * POST
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {Array} updates - Array of account update objects
 * @returns { successful: number, failed: [{ id, gl_key, reason, validation_errors }] }
 */
export const bulkUpdateGLKeys = async (accessToken, projectId, updates) => {
  try {
    validateParams({ accessToken, projectId, updates }, 'bulkUpdateGLKeys');
    const response = await axios({
      data: {
        project_id: projectId,
        updates: updates
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${projectsDataUrl}bulk-update`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Bulk update monthly values for multiple accounts
 * PUT
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {Array} updates - Array of { gl_key: string, dates: { "YYYY-MM-DD": number } }
 * @returns { successful: number, failed: [{ gl_key, reason }] }
 */
export const bulkUpdateMonthlyValues = async (accessToken, projectId, updates) => {
  try {
    validateParams({ accessToken, projectId, updates }, 'bulkUpdateMonthlyValues');
    const response = await axios({
      data: {
        updates: updates
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PUT',
      url: `${projectsDataUrl}${projectId}/bulk-update-dates`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**********************************
 *                                *
 *           CHANGES              *
 *                                *
 *********************************/

/**
 * POST
 * @param {Object} changeData
 * @param {Number} userId
 * @param {Number} projectId
 * @param {String} accessToken Auth token
 * @returns Newly created change
 */
export const createChange = async (changeData, userId, projectId, accessToken) => {
  debugLogger.log('API_CHANGE', 'Creating change', { 
    changeLabel: changeData.change_label,
    functionOperator: changeData.function_operator,
    selectedAccountsCount: changeData.selected_accounts?.length,
    dateRange: changeData.date_range,
    isMasterStat: changeData.is_master_stat_change,
    isStatDriver: changeData.is_stat_driver,
    isDominant: changeData.is_dominant_change
  });
  
  try {
    validateParams({ userId, projectId, accessToken }, 'createChange');
    const response = await axios({
      data: {
        'change_label': changeData.change_label,
        'date_range': changeData.date_range,
        'date_range_select': changeData.date_range_select || 'custom',
        'driven_accounts': changeData.driven_accounts,
        'function_operator': changeData.function_operator,
        'is_dominant_change': changeData.is_dominant_change,
        'is_stat_driver': changeData.is_stat_driver,
        'operator_value': changeData.operator_value,
        'reference_accounts': changeData.reference_accounts,
        'ref_account_formula': changeData.ref_account_formula,
        'selected_accounts': changeData.selected_accounts,
        'user_note': changeData.user_note,
        'parent_change': changeData.parent_change,
        'user_id': userId,
        'project_id': projectId,
        'monthly_values': changeData.monthly_values,
        'dates': changeData.dates, // Dates field for Master Stat distribution and selective month changes
        // Master Stat fields
        'is_master_stat_change': changeData.is_master_stat_change || false,
        'master_stat_gl_keys': changeData.master_stat_gl_keys || null,
        'affected_master_stats': changeData.affected_master_stats || null,
        'child_stat_adjustments': changeData.child_stat_adjustments || null,
        'affects_master_stats_via_children': changeData.affects_master_stats_via_children || false,
        'affected_master_stats_by_children': changeData.affected_master_stats_by_children || null,
        'affects_stat_drivers': changeData.affects_stat_drivers || false,
        'affected_stat_drivers': changeData.affected_stat_drivers || null,
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: changesUrl,
    });
    
    debugLogger.log('API_CHANGE', 'Change created successfully', { 
      changeId: response.data?.id,
      changeLabel: changeData.change_label
    });
    
    return response;
  } catch (error) {
    debugLogger.error('API_CHANGE', 'Failed to create change', error);
    return error;
  }
};

/**
 * PUT
 * @param {Object} changeData
 * @param {Number} userId
 * @param {Number} projectId
 * @param {String} accessToken Auth token
 * @returns Newly edited change
 */
export const editChange = async (changeData, userId, projectId, accessToken) => {
  try {
    validateParams({ userId, projectId, accessToken }, 'editChange');
    const response = await axios({
      data: {
        'id': changeData.id,
        'change_label': changeData.change_label,
        'date_range': changeData.date_range,
        'date_range_select': changeData.date_range_select || 'custom',
        'driven_accounts': changeData.driven_accounts,
        'function_operator': changeData.function_operator,
        'is_dominant_change': changeData.is_dominant_change,
        'is_stat_driver': changeData.is_stat_driver,
        'operator_value': changeData.operator_value,
        'reference_accounts': changeData.reference_accounts,
        'ref_account_formula': changeData.ref_account_formula,
        'selected_accounts': changeData.selected_accounts,
        'user_note': changeData.user_note,
        'parent_change': changeData.parent_change,
        'user_id': userId,
        'project_id': projectId,
        'dates': changeData.dates, // Dates field for Master Stat distribution and selective month changes
        // Master Stat fields
        'is_master_stat_change': changeData.is_master_stat_change || false,
        'master_stat_gl_keys': changeData.master_stat_gl_keys || null,
        'affected_master_stats': changeData.affected_master_stats || null,
        'child_stat_adjustments': changeData.child_stat_adjustments || null,
        'affects_master_stats_via_children': changeData.affects_master_stats_via_children || false,
        'affected_master_stats_by_children': changeData.affected_master_stats_by_children || null,
        'affects_stat_drivers': changeData.affects_stat_drivers || false,
        'affected_stat_drivers': changeData.affected_stat_drivers || null,
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PUT',
      url: changesUrl,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * DELETE
 * @param {Number} changeId
 * @param {String} accessToken Auth token
 * @returns Success or error message
 */
export const deleteChange = async (changeId, accessToken) => {
  try {
    validateParams({ changeId, accessToken }, 'deleteChange');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${changesUrl}${changeId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};


export const getAllChangesByProjectId = async (projectId, accessToken) => {
  try {
    validateParams({ projectId, accessToken }, 'getAllChangesByProjectId');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${changesUrl}project/${projectId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
}

/**
 * GET audit trail for a change
 * @param {Number} changeId - ID of the change
 * @param {String} accessToken - Auth token
 * @returns Audit trail entries in chronological order
 */
export const getChangeAuditTrail = async (changeId, accessToken) => {
  try {
    validateParams({ changeId, accessToken }, 'getChangeAuditTrail');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${changesUrl}${changeId}/audit-trail`,
    });
    return response;
  } catch (error) {
    return error;
  }
}

/**
 * POST a comment to a change
 * @param {Number} changeId - ID of the change
 * @param {String} commentText - Comment text (1-1000 characters)
 * @param {String} accessToken - Auth token
 * @returns Created audit trail entry with 201 status
 */
export const addCommentToChange = async (changeId, commentText, accessToken) => {
  try {
    validateParams({ changeId, commentText, accessToken }, 'addCommentToChange');
    const response = await axios({
      data: {
        comment_text: commentText,
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${changesUrl}${changeId}/comments`,
    });
    return response;
  } catch (error) {
    return error;
  }
}

/**
 * GET audit trail for a Master Stat
 * @param {Number} projectId - Project ID
 * @param {Number} masterStatId - Master Stat ID
 * @param {String} accessToken - Auth token
 * @returns Audit trail entries in chronological order
 */
export const getMasterStatAuditTrail = async (projectId, masterStatId, accessToken) => {
  try {
    validateParams({ projectId, masterStatId, accessToken }, 'getMasterStatAuditTrail');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${masterStatsUrl}${projectId}/${masterStatId}/audit-trail`,
    });
    return response;
  } catch (error) {
    return error;
  }
}

/**
 * POST a comment to a Master Stat
 * @param {Number} projectId - Project ID
 * @param {Number} masterStatId - Master Stat ID
 * @param {String} commentText - Comment text (1-1000 characters)
 * @param {String} accessToken - Auth token
 * @returns Created audit trail entry with 201 status
 */
export const addCommentToMasterStat = async (projectId, masterStatId, commentText, accessToken) => {
  try {
    validateParams({ projectId, masterStatId, commentText, accessToken }, 'addCommentToMasterStat');
    const response = await axios({
      data: {
        comment_text: commentText,
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${masterStatsUrl}${projectId}/${masterStatId}/comments`,
    });
    return response;
  } catch (error) {
    return error;
  }
}




/**********************************
 *                                *
 *           ENTITIES             *
 *                                *
 *********************************/

  /**
 * GET a project entities
 * @param {Number} id Project id
 * @param {String} accessToken auth token
 * @returns Project entities
 */
export const getProjectEntities = async (id, accessToken) => {
  try {
    validateParams({ id, accessToken }, 'getProjectEntities');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsAggregationUrl}entities/${id}`,
    })
    return response;
  } catch (error) {
    return error;
  }
}

/**
 * Create a new entity
 * POST /taxonomy/projects/<projectId>/entities
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {Object} entityData - { entity_id: string, entity_name: string }
 * @returns Created entity object { id, project_id, entity_id, entity_name }
 */
export const createEntity = async (accessToken, projectId, entityData) => {
  try {
    validateParams({ accessToken, projectId, entityData }, 'createEntity');
    const response = await axios({
      data: entityData,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${baseUrl}/taxonomy/projects/${projectId}/entities`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Rename an entity
 * PUT /taxonomy/projects/<projectId>/entities/<entityId>
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {String} entityId - Entity ID to update
 * @param {Object} entityData - { entity_name: string }
 * @returns Updated entity object { id, project_id, entity_id, entity_name }
 */
export const updateEntity = async (accessToken, projectId, entityId, entityData) => {
  try {
    validateParams({ accessToken, projectId, entityId, entityData }, 'updateEntity');
    const response = await axios({
      data: entityData,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PUT',
      url: `${baseUrl}/taxonomy/projects/${projectId}/entities/${entityId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Delete an entity
 * DELETE /taxonomy/projects/<projectId>/entities/<entityId>
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {String} entityId - Entity ID to delete
 */
export const deleteEntity = async (accessToken, projectId, entityId) => {
  try {
    validateParams({ accessToken, projectId, entityId }, 'deleteEntity');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${baseUrl}/taxonomy/projects/${projectId}/entities/${entityId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};


/**********************************
 *                                *
 *         DEPARTMENTS            *
 *                                *
 *********************************/

  /**
 * GET a project departments
 * @param {Number} id Project id
 * @param {String} entities Entities list
 * @param {String} accessToken auth token
 * @returns Project departments
 */
export const getProjectDepartments = async (id, entities, accessToken) => {
  try {
    validateParams({ id, entities, accessToken }, 'getProjectDepartments');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${projectsAggregationUrl}departments/${id}/${entities}`,
    })
    return response;
  } catch (error) {
    return error;
  }
}

/**
 * Create a new department
 * POST /taxonomy/projects/<projectId>/entities/<entityId>/departments
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {Object} departmentData - { entity_id: string, department_id: string, department_name: string }
 * @returns Created department object { id, project_id, entity_id, department_id, department_name }
 */
export const createDepartment = async (accessToken, projectId, departmentData) => {
  try {
    validateParams({ accessToken, projectId, departmentData }, 'createDepartment');
    const response = await axios({
      data: departmentData,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${baseUrl}/taxonomy/projects/${projectId}/entities/${departmentData.entity_id}/departments`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Rename a department
 * PUT /taxonomy/projects/<projectId>/entities/<entityId>/departments/<departmentId>
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {String} entityId - Entity ID
 * @param {String} departmentId - Department ID to update
 * @param {Object} departmentData - { department_name: string }
 * @returns Updated department object { id, project_id, entity_id, department_id, department_name }
 */
export const updateDepartment = async (accessToken, projectId, entityId, departmentId, departmentData) => {
  try {
    validateParams({ accessToken, projectId, entityId, departmentId, departmentData }, 'updateDepartment');
    const response = await axios({
      data: departmentData,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PUT',
      url: `${baseUrl}/taxonomy/projects/${projectId}/entities/${entityId}/departments/${departmentId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Delete a department
 * DELETE /taxonomy/projects/<projectId>/entities/<entityId>/departments/<departmentId>
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {String} entityId - Entity ID
 * @param {String} departmentId - Department ID to delete
 */
export const deleteDepartment = async (accessToken, projectId, entityId, departmentId) => {
  try {
    validateParams({ accessToken, projectId, entityId, departmentId }, 'deleteDepartment');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${baseUrl}/taxonomy/projects/${projectId}/entities/${entityId}/departments/${departmentId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**********************************
 *                                *
 *           ACCOUNTS             *
 *                                *
 *********************************/

/**
 * GET a project accounts
 * @param {Number} id Project id
 * @param {Array} data An array of objects structured as follows: [{entity_id: "1", departments_id: ["1100"]}]
 * @param {String} accessToken auth token
 * @returns Project accounts
 */
export const getProjectAccounts = async (id, data, accessToken) => {
  try {
    validateParams({ id, data, accessToken }, 'getProjectAccounts');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${projectsAggregationUrl}accounts/${id}`,
      data: data
    });
    return response;
  } catch (error) {
    return error;
  }
}

/**
 * POST project accounts with pagination (optimized for large datasets)
 * @param {Number} projectId - Project id
 * @param {Object} options - Pagination options
 * @param {Array} options.entity_ids - Optional: filter by entity IDs
 * @param {Array} options.department_ids - Optional: filter by department IDs
 * @param {Number} options.page - Page number (1-indexed, default: 1)
 * @param {Number} options.page_size - Items per page (default: 1000, max: 5000)
 * @param {Boolean} options.include_totals - Include aggregated totals (default: false)
 * @param {String} accessToken - Auth token
 * @returns {Promise} Response with accounts array and pagination metadata
 */
export const getProjectAccountsPaginated = async (projectId, options = {}, accessToken) => {
  try {
    validateParams({ projectId, accessToken }, 'getProjectAccountsPaginated');
    
    debugLogger.log('API_PROJECT', 'Loading paginated accounts', { 
      projectId, 
      page: options.page || 1,
      pageSize: options.page_size || 1000,
      hasFilters: !!(options.entity_ids || options.department_ids)
    });
    
    const response = await withTimeout(
      axios({
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
        method: 'POST',
        url: `${projectsDataUrl}${projectId}/paginated`,
        data: {
          entity_ids: options.entity_ids,
          department_ids: options.department_ids,
          page: options.page || 1,
          page_size: options.page_size || 1000,
          include_totals: options.include_totals || false
        }
      }),
      30000,
      'getProjectAccountsPaginated'
    );
    
    debugLogger.log('API_PROJECT', 'Paginated accounts loaded', {
      projectId,
      accountsReturned: response.data.accounts?.length || 0,
      totalCount: response.data.pagination?.total_count || 0,
      page: response.data.pagination?.page || 1,
      totalPages: response.data.pagination?.total_pages || 1
    });
    
    return response;
  } catch (error) {
    debugLogger.error('API_PROJECT', 'Failed to load paginated accounts', error);
    return error;
  }
}

/**
 * GET all accounts for a project WITHOUT dates (metadata only).
 * Fast even for 200k+ account projects — used for initial project load.
 * @param {Number} projectId - Project id
 * @param {String} accessToken - Auth token
 * @returns {Promise} Response with flat accounts array (no dates field)
 */
export const getProjectAccountsMetadata = async (projectId, accessToken) => {
  try {
    validateParams({ projectId, accessToken }, 'getProjectAccountsMetadata');

    // Fetch all accounts by paginating through the endpoint.
    // The backend defaults to limit=1000; projects can have more accounts than that.
    const pageSize = 10000; // max allowed by the backend
    let offset = 0;
    let allAccounts = [];
    // Initialize to Infinity so the do-while loop always executes at least once.
    // totalCount is updated from the first response before the condition is checked.
    let totalCount = Infinity;

    do {
      const response = await withTimeout(
        axios({
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-type': 'application/json',
          },
          method: 'GET',
          url: `${projectsDataUrl}metadata/${projectId}?limit=${pageSize}&offset=${offset}`,
        }),
        30000,
        'getProjectAccountsMetadata'
      );

      if (!response?.data?.accounts) {
        // Return the raw response so the caller can handle errors
        return response;
      }

      allAccounts = allAccounts.concat(response.data.accounts);
      totalCount = response.data.total_count;
      offset += pageSize;
    } while (offset < totalCount);

    // Return a response-shaped object with all accounts merged
    return {
      status: 200,
      data: { accounts: allAccounts, total_count: totalCount },
    };
  } catch (error) {
    debugLogger.error('API_PROJECT', 'Failed to load account metadata', error);
    return error;
  }
}

/**
 * GET children accounts for a specific parent account (lazy loading)
 * @param {Number} projectId - Project id
 * @param {String} parentAccount - Parent account identifier
 * @param {String} accessToken - Auth token
 * @returns {Promise} Response with children array
 */
export const getParentAccountChildren = async (projectId, parentAccount, accessToken) => {
  try {
    validateParams({ projectId, parentAccount, accessToken }, 'getParentAccountChildren');
    
    debugLogger.log('API_PROJECT', 'Loading parent account children', { 
      projectId, 
      parentAccount
    });
    
    const response = await withTimeout(
      axios({
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
        method: 'GET',
        url: `${projectsAggregationUrl}parent-accounts/${projectId}/${encodeURIComponent(parentAccount)}/children`
      }),
      10000,
      'getParentAccountChildren'
    );
    
    debugLogger.log('API_PROJECT', 'Parent account children loaded', {
      projectId,
      parentAccount,
      childCount: response.data.child_count || 0
    });
    
    return response;
  } catch (error) {
    debugLogger.error('API_PROJECT', 'Failed to load parent account children', error);
    return error;
  }
}

/**
 * GET department summaries for a specific parent account (hierarchical lazy loading - level 1)
 * @param {Number} projectId - Project id
 * @param {String} parentAccount - Parent account identifier
 * @param {String} accessToken - Auth token
 * @returns {Promise} Response with departments array
 */
export const getParentAccountDepartments = async (projectId, parentAccount, accessToken) => {
  try {
    validateParams({ projectId, parentAccount, accessToken }, 'getParentAccountDepartments');

    debugLogger.log('API_PROJECT', 'Loading parent account departments', { projectId, parentAccount });

    const response = await withTimeout(
      axios({
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
        method: 'GET',
        url: `${projectsAggregationUrl}parent-accounts/${projectId}/${encodeURIComponent(parentAccount)}/departments`
      }),
      15000,
      'getParentAccountDepartments'
    );

    debugLogger.log('API_PROJECT', 'Parent account departments loaded', {
      projectId,
      parentAccount,
      deptCount: response.data.department_count || 0
    });

    return response;
  } catch (error) {
    debugLogger.error('API_PROJECT', 'Failed to load parent account departments', error);
    return error;
  }
}

/**
 * GET accounts for a specific parent account + department (hierarchical lazy loading - level 2)
 * @param {Number} projectId - Project id
 * @param {String} parentAccount - Parent account identifier
 * @param {String} departmentId - Department id
 * @param {String} accessToken - Auth token
 * @returns {Promise} Response with children array
 */
export const getParentAccountDepartmentChildren = async (projectId, parentAccount, departmentId, accessToken) => {
  try {
    validateParams({ projectId, parentAccount, departmentId, accessToken }, 'getParentAccountDepartmentChildren');

    debugLogger.log('API_PROJECT', 'Loading dept children', { projectId, parentAccount, departmentId });

    const response = await withTimeout(
      axios({
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
        method: 'GET',
        url: `${projectsAggregationUrl}parent-accounts/${projectId}/${encodeURIComponent(parentAccount)}/departments/${encodeURIComponent(departmentId)}/children`
      }),
      10000,
      'getParentAccountDepartmentChildren'
    );

    debugLogger.log('API_PROJECT', 'Dept children loaded', {
      projectId,
      parentAccount,
      departmentId,
      childCount: response.data.child_count || 0
    });

    return response;
  } catch (error) {
    debugLogger.error('API_PROJECT', 'Failed to load dept children', error);
    return error;
  }
}

  /**
 * GET account full details
 * @param {Number} id Project id
 * @param {String} entity_id Entity id
 * @param {String} department_id Department id
 * @param {String} account_id Account id
 * @param {String} accessToken auth token
 * @returns Project accounts
 */
  export const getAccountFullDetails = async (id, entity_id, department_id, account_id, accessToken) => {
    try {
      validateParams({ id, entity_id, department_id, account_id, accessToken }, 'getAccountFullDetails');
      const response = await axios({
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
        method: 'GET',
        url: `${projectsAggregationUrl}accounts/${id}/${entity_id}/${department_id}/${account_id}`
      })
      return response
    } catch (error) {
      return error
    }
  }

/**
 * Create a new parent account
 * POST /taxonomy/projects/<projectId>/parent-accounts
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {Object} data - { parent_account_name: string }
 * @returns Created parent account object { id, project_id, parent_account_name }
 */
export const createParentAccount = async (accessToken, projectId, data) => {
  try {
    validateParams({ accessToken, projectId, data }, 'createParentAccount');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${baseUrl}/taxonomy/projects/${projectId}/parent-accounts`,
      data: data,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * GET parent accounts list for a project
 * GET /taxonomy/projects/<projectId>/parent-accounts
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @returns Array of parent account objects { id, project_id, parent_account_name }
 */
export const getParentAccounts = async (accessToken, projectId) => {
  try {
    validateParams({ accessToken, projectId }, 'getParentAccounts');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${baseUrl}/taxonomy/projects/${projectId}/parent-accounts`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Rename a parent account
 * PUT /taxonomy/projects/<projectId>/parent-accounts/<parentAccountId>
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {Number} parentAccountId - Integer PK of the parent account
 * @param {Object} data - { parent_account_name: string }
 * @returns Updated parent account object { id, project_id, parent_account_name }
 */
export const updateParentAccount = async (accessToken, projectId, parentAccountId, data) => {
  try {
    validateParams({ accessToken, projectId, parentAccountId, data }, 'updateParentAccount');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PUT',
      url: `${baseUrl}/taxonomy/projects/${projectId}/parent-accounts/${parentAccountId}`,
      data: data,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Delete a parent account
 * DELETE /taxonomy/projects/<projectId>/parent-accounts/<parentAccountId>
 * @param {String} accessToken - Auth token
 * @param {Number} projectId - Project ID
 * @param {Number} parentAccountId - Integer PK of the parent account
 */
export const deleteParentAccount = async (accessToken, projectId, parentAccountId) => {
  try {
    validateParams({ accessToken, projectId, parentAccountId }, 'deleteParentAccount');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${baseUrl}/taxonomy/projects/${projectId}/parent-accounts/${parentAccountId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};


/**********************************
 *                                *
 *        BACKUP_EXPORTS          *
 *                                *
 *********************************/

  export const listBackupExports = async (accessToken) => {
    try {
      const response = await axios({
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
        method: 'GET',
        url: `${backupExportsUrl}list`
      })
      return response
    } catch (error) {
      return error
    }
  }

  export const deleteBackups = async (backupExportIds, accessToken) => {
    try {
      const response = await axios({
        data: {
          backup_export_ids: backupExportIds,
        },
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-type': 'application/json',
        },
        method: 'DELETE',
        url: `${backupExportsUrl}`
      });
      return response;
    } catch (error) {
      return error;
    }
  };

/**********************************
 *                                *
 *         MASTER STATS           *
 *                                *
 *********************************/

/**
 * Create a new Master Stat
 * POST
 * @param {Object} masterStatData - Master Stat configuration
 * @param {String} userId - User ID
 * @param {String} projectId - Project ID
 * @param {String} accessToken - Auth token
 * @returns Created Master Stat object
 */
export const createMasterStat = async (masterStatData, userId, projectId, accessToken) => {
  try {
    validateParams({ masterStatData, userId, projectId, accessToken }, 'createMasterStat');
    const response = await axios({
      data: { ...masterStatData, user_id: userId },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${masterStatsUrl}${projectId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Get all Master Stats for a project
 * GET
 * @param {String} projectId - Project ID
 * @param {String} accessToken - Auth token
 * @returns Array of Master Stats
 */
export const getMasterStats = async (projectId, accessToken) => {
  try {
    validateParams({ projectId, accessToken }, 'getMasterStats');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${masterStatsUrl}${projectId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Update an existing Master Stat
 * PUT
 * @param {String} masterStatId - Master Stat ID
 * @param {Object} masterStatData - Updated Master Stat configuration
 * @param {String} userId - User ID
 * @param {String} projectId - Project ID
 * @param {String} accessToken - Auth token
 * @returns Updated Master Stat object
 */
export const updateMasterStat = async (masterStatId, masterStatData, userId, projectId, accessToken) => {
  try {
    validateParams({ masterStatId, masterStatData, userId, projectId, accessToken }, 'updateMasterStat');
    const response = await axios({
      data: { ...masterStatData, user_id: userId },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PUT',
      url: `${masterStatsUrl}${projectId}/${masterStatId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Delete a Master Stat
 * DELETE
 * @param {String} masterStatId - Master Stat ID
 * @param {String} projectId - Project ID
 * @param {String} accessToken - Auth token
 * @returns Success or error message
 */
export const deleteMasterStat = async (masterStatId, projectId, accessToken) => {
  try {
    validateParams({ masterStatId, projectId, accessToken }, 'deleteMasterStat');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${masterStatsUrl}${projectId}/${masterStatId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Get User Project Access
 * GET
 * @param {Number} userId - User ID
 * @param {String} accessToken - Auth token
 * @returns List of projects the user has access to
 */
export const getUserProjectAccess = async (userId, accessToken) => {
  try {
    validateParams({ userId, accessToken }, 'getUserProjectAccess');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${getBaseUrl()}/projects/users/${userId}/projects`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Update User Project Access
 * POST
 * @param {Number} userId - User ID
 * @param {Array} projectIds - Array of project IDs to grant access to
 * @param {String} accessToken - Auth token
 * @returns Success message with added/removed counts
 */
export const updateUserProjectAccess = async (userId, projectIds, accessToken) => {
  try {
    validateParams({ userId, projectIds, accessToken }, 'updateUserProjectAccess');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${getBaseUrl()}/projects/users/${userId}/projects`,
      data: { project_ids: projectIds },
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Create Project Asynchronously
 * POST /projects/async
 * Initiates async project creation and returns job ID for tracking
 * @param {Object} projectData - Project data from NewProjectSchema
 * @param {String} accessToken - Auth token
 * @returns {Object} { job_id, message, status_url }
 */
export const createProjectAsync = async (projectData, accessToken) => {
  try {
    validateParams({ projectData, accessToken }, 'createProjectAsync');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${getBaseUrl()}/projects/async`,
      data: projectData,
      timeout: 120000, // 2 minute timeout for large projects
    });
    return response;
  } catch (error) {
    console.error('[CREATE_PROJECT_ASYNC] Error during async project creation', {
      message: error?.message,
      code: error?.code,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      responseMessage: error?.response?.data?.message,
      isTimeout: error?.code === 'ECONNABORTED',
    });
    return error;
  }
};

/**
 * Get Job Status
 * GET /projects/jobs/{job_id}
 * Retrieves status of an async project creation job
 * @param {String} jobId - Job ID
 * @param {String} accessToken - Auth token
 * @returns {Object} Job status with progress information
 */
export const getJobStatus = async (jobId, accessToken) => {
  try {
    validateParams({ jobId, accessToken }, 'getJobStatus');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${getBaseUrl()}/projects/jobs/${jobId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Get User Jobs
 * GET /projects/jobs
 * Retrieves recent jobs for the current user
 * @param {String} accessToken - Auth token
 * @param {Number} limit - Maximum number of jobs to return (default: 10)
 * @returns {Object} { jobs: [...] }
 */
export const getUserJobs = async (accessToken, limit = 10) => {
  try {
    validateParams({ accessToken }, 'getUserJobs');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${getBaseUrl()}/projects/jobs?limit=${limit}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * Cancel Job
 * DELETE /projects/jobs/{job_id}
 * Cancels an in-progress project creation job
 * @param {String} jobId - Job ID
 * @param {String} accessToken - Auth token
 * @returns {Object} { message, job_id }
 */
export const cancelJob = async (jobId, accessToken) => {
  try {
    validateParams({ jobId, accessToken }, 'cancelJob');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${getBaseUrl()}/projects/jobs/${jobId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**********************************
 *                                *
 *            INVITE              *
 *                                *
 *********************************/

/**
 * Look up a valid invite token and return the associated user's name and email.
 * No auth required — the token itself is the credential.
 * @param {string} token - The invite token from the URL
 * @returns {Promise} Resolves with { first_name, last_name, email } or 404 error
 */
export const getInviteTokenInfo = async (token) => {
  try {
    validateParams({ token }, 'getInviteTokenInfo');
    const response = await axios({
      headers: { 'Content-type': 'application/json' },
      method: 'GET',
      url: `${inviteUrl}token-info?token=${encodeURIComponent(token)}`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Set a new user's password using a valid invite token.
 * Returns a JWT on success so the user is immediately authenticated.
 * @param {string} token - The invite token from the URL
 * @param {string} newPassword - The password the user has chosen
 * @returns {Promise} Resolves with { access_token, message } on success
 */
export const setPasswordFromInvite = async (token, newPassword) => {
  try {
    validateParams({ token, newPassword }, 'setPasswordFromInvite');
    const response = await axios({
      data: {
        invite_token: token,
        new_password: newPassword,
      },
      headers: { 'Content-type': 'application/json' },
      method: 'POST',
      url: `${inviteUrl}set-password`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Resend an invite email to a pending user.
 * Requires admin JWT.
 * Returns the full Axios response on both success and error so callers can
 * inspect delivery_status from 207/500 response bodies without try/catch.
 * @param {number} userId - The ID of the pending user
 * @param {string} accessToken - Admin JWT
 * @returns {Promise} Always resolves (never throws) — check response.status
 */
export const resendInvite = async (userId, accessToken) => {
  try {
    validateParams({ userId, accessToken }, 'resendInvite');
    const response = await axios({
      data: { user_id: userId },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${inviteUrl}resend`,
    });
    return response;
  } catch (error) {
    // Return the error response rather than throwing so callers can read
    // delivery_status from 207/500 response bodies without try/catch.
    return error?.response || error;
  }
};

/**
 * Cancel a pending invite for a user — deletes the user account entirely.
 * @param {number} userId - The ID of the pending user
 * @param {string} accessToken - Admin JWT
 * @returns {Promise} Resolves with { message } on success
 */
export const cancelInvite = async (userId, accessToken) => {
  try {
    validateParams({ userId, accessToken }, 'cancelInvite');
    const response = await axios({
      data: { user_id: userId },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: `${inviteUrl}cancel`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Update the email address on a pending invite and immediately resend.
 * Returns the full Axios response on both success and error so callers can
 * inspect delivery_status without needing a separate GET /users/ call.
 * @param {number} userId - The ID of the pending user
 * @param {string} newEmail - The corrected email address
 * @param {string} accessToken - Admin JWT
 * @returns {Promise} Always resolves (never throws) — check response.status
 */
export const updateInviteEmail = async (userId, newEmail, accessToken) => {
  try {
    validateParams({ userId, newEmail, accessToken }, 'updateInviteEmail');
    const response = await axios({
      data: { user_id: userId, new_email: newEmail },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PATCH',
      url: `${inviteUrl}update-email`,
    });
    return response;
  } catch (error) {
    // Return the error response object rather than throwing so callers can
    // display the server error message without a try/catch at the call site.
    return error?.response || error;
  }
};



/**********************************
 *                                *
 *       SAVED FILTER VIEWS       *
 *                                *
 *********************************/

const savedViewsUrl = `${baseUrl}/saved-views/`;

/**
 * GET all saved filter views for the current user on a project.
 * @param {Number} projectId
 * @param {String} accessToken
 * @returns Array of saved view objects
 */
export const getSavedViews = async (projectId, accessToken) => {
  try {
    validateParams({ projectId, accessToken }, 'getSavedViews');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'GET',
      url: `${savedViewsUrl}project/${projectId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * POST a new saved filter view.
 * @param {Number} projectId
 * @param {Number} userId      — used by backend to verify identity (JWT-derived server-side)
 * @param {String} name
 * @param {Object} filterState  — plain JS object (Sets already serialised to arrays by caller)
 * @param {Boolean} isDefault
 * @param {String} accessToken
 * @returns Created saved view object
 */
export const createSavedView = async (projectId, userId, _organizationId, name, filterState, isDefault, accessToken) => {
  try {
    validateParams({ projectId, userId, name, accessToken }, 'createSavedView');
    const response = await axios({
      data: {
        project_id: projectId,
        name,
        filter_state: filterState,
        is_default: isDefault,
      },
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'POST',
      url: savedViewsUrl,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * PATCH an existing saved view (rename, update filter_state, or toggle is_default).
 * @param {Number} viewId
 * @param {Object} patch  — any subset of { name, filter_state, is_default }
 * @param {String} accessToken
 * @returns Updated saved view object
 */
export const updateSavedView = async (viewId, patch, accessToken) => {
  try {
    validateParams({ viewId, accessToken }, 'updateSavedView');
    const response = await axios({
      data: patch,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'PATCH',
      url: `${savedViewsUrl}${viewId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};

/**
 * DELETE a saved view.
 * @param {Number} viewId
 * @param {String} accessToken
 */
export const deleteSavedView = async (viewId, accessToken) => {
  try {
    validateParams({ viewId, accessToken }, 'deleteSavedView');
    const response = await axios({
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-type': 'application/json',
      },
      method: 'DELETE',
      url: `${savedViewsUrl}${viewId}`,
    });
    return response;
  } catch (error) {
    return error;
  }
};
